import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Input,
  Tab,
  TabList,
  Tabs,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';
import {
  ComponentBanner,
  DateFilter,
  useMetricsTableStore,
  CircleCheck,
  useDebounce,
  DownloadIcon,
  useToastHook,
  useGlobalState,
} from '@devd-client/devd/components';
import { getOrgId } from '@devd-client/devd/auth';
import { OptionBase, Select } from 'chakra-react-select';
import {
  customFilterSelect,
  filterStyles,
  repoauthorFilterStyles,
} from './FilterStyles';
import {
  TeamHierarchySelectOption,
  useTeamHierarchy,
  flattenTeamsWithMetadata,
} from '@devd-client/devd/components';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import SearchBar from './SearchBar';
import useMetricTableHelpers from '../hooks/useMetricTableHelpers';
import SubHeading from './SubHeading';
import useHandleOptionChange from '../hooks/useHandleOptionChange';
import useOptions from '../hooks/useOptions';
import useHandleStatusChange from '../hooks/useHandleStatusChange';
import {
  FilterByType,
  GetOptionsType,
  NavigationState,
} from '../../../utils/metricTableTypes';
import { handleSelectPlaceHolder } from '../../../utils/metricTable.utils';
import { useDownloadCSV } from '../../../apis';
interface Option extends OptionBase {
  label: string;
  value: string;
  depth?: number;
  teamPath?: string;
  hasNestedSubTeams?: boolean;
}
interface PrFilterType {
  isFetched: boolean;
  startDate: string;
  endDate: string;
  chartKey: string;
  metricKey: string;
  heading: string;
  totalDBrows: number;
  firstOption: string;
  secondOption: string;
  teamId: string;
  status: string;
  setTeamId: (value: string) => void;
  setStatus: (value: string) => void;
  setFirstOption: (value: string) => void;
  setSecondOption: (value: string) => void;
  setChartKey: (value: string) => void;
  setMetric: (value: string) => void;
  setMetricKey: (value: string) => void;
  setHeading: (value: string) => void;
  setBranches: (value: string) => void;
  branches: string;
  sprintId: string;
}
// This component consists all filters used in PR table.
export default function PrFilters({
  isFetched,
  startDate,
  endDate,
  heading,
  chartKey,
  metricKey,
  totalDBrows,
  firstOption,
  secondOption,
  teamId,
  status,
  setTeamId,
  setStatus,
  setSecondOption,
  setFirstOption,
  setChartKey,
  setMetric,
  setHeading,
  setMetricKey,
  setBranches,
  branches,
  sprintId,
}: PrFilterType) {
  const location = useLocation();
  const state = location.state as NavigationState;
  const [searchParams] = useSearchParams();
  const [branchInput, setBranchInput] = useState(branches || '');
  const debouncedBranchInput = useDebounce(branchInput, 500);
  const { colors } = useTheme();
  const [clearSearchBar, setClearSearchBar] = useState<null | boolean>(null);
  const [setToast] = useToastHook();
  const metricFilterBy = useMetricsTableStore();
  const { handleMetricFilterBy, handleScrollToTop } = useMetricTableHelpers();
  const { mutate: downloadCSV, isLoading: isDownloadingCSV } = useDownloadCSV();
  const { globalState } = useGlobalState();
  const downloadAllowed = globalState?.dto?.downloadAllowed;

  const handleDownloadCSV = () => {
    if (!downloadAllowed) {
      setToast({
        message: 'You need to upgrade your plan to download data',
        status: 'error',
      });
      return;
    }
    downloadCSV(
      {
        filter: {
          startDate,
          endDate,
          orgId: getOrgId(),
          teamId: teamId,
          author: metricFilterBy.author,
          reviewer: metricFilterBy.reviewer,
          branches: metricFilterBy.branches,
          sprintId: sprintId,
          project: '',
          projectsFilter: metricFilterBy.projectsFilter,
          type: metricFilterBy.type,
          status:
            metricFilterBy.status === 'all' ? null : metricFilterBy.status,
          prViewFilters: {
            prSize: metricFilterBy.prSize,
            age: metricFilterBy.filterAge ?? null,
            prIds: metricFilterBy.prIds,
            searchQuery: metricFilterBy.searchQuery,
            prReviewResponsivenessType:
              metricFilterBy.prReviewResponsivenessType,
            prFeedbackResponsivenessType:
              metricFilterBy.prFeedbackResponsivenessType,
            isPrReviewedWithComment: metricFilterBy.isPrReviewedWithComment,
            isPrLinked: metricFilterBy.isPrLinked,
            isPrWithComment: metricFilterBy.isPrWithComment,
            isLongPrCycleTime: metricFilterBy.isLongPrCycleTime,
            isMergedWithReview: metricFilterBy.isMergedWithReview,
            prIterations: metricFilterBy.prIterations,
          },
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        metric: metricFilterBy.metric,
      },
      {
        onSuccess(data) {
          setToast({
            message: data.dto,
            status: 'success',
          });
        },
        onError(error) {
          setToast({
            message: (error as any).message,
            status: 'error',
          });
        },
      }
    );
  };

  const { getOptions, contributorData, issueOptions, repoList, teamData } =
    useOptions({
      startDate,
      endDate,
    });

  const {
    showOption,
    teamValue,
    defaultValue,
    secondaryDefaultValue,
    secondaryDefaultMultiValue,
    handleTeamOptionChange,
    handleShowOptionChange,
    handleDefaultOptionChange,
    handleSecondaryDefaultOptionChange,
    handleSecondaryDefaultMultiOptionChange,
  } = useHandleOptionChange();
  const { handlePRStatusChange, tabIndex, setTabIndex } = useHandleStatusChange(
    {
      setStatus,
    }
  );

  const allTeamOptions = useMemo(
    () => flattenTeamsWithMetadata(teamData?.dto ?? []),
    [teamData]
  );

  const { expandedTeamPaths, toggleTeamExpand, visibleTeams } =
    useTeamHierarchy(allTeamOptions);

  useEffect(() => {
    handleDefaultOptionChange(
      getOptions('prTable').filter(
        (option: Option) => option.value === (firstOption ?? '')
      )?.[0]?.label,
      firstOption ?? ''
    );
    if (firstOption === 'repos') {
      handleSecondaryDefaultMultiOptionChange(
        getOptions(firstOption as GetOptionsType)?.filter((option: Option) =>
          secondOption?.split(',')?.includes(option.value)
        ) || []
      );
    } else {
      handleSecondaryDefaultOptionChange(
        getOptions(firstOption as GetOptionsType)?.filter(
          (option: Option) => option.value === (secondOption ?? '')
        )?.[0]?.label,
        secondOption ?? ''
      );
    }
    handleTeamOptionChange(teamId ?? '');
    handleShowOptionChange((firstOption as GetOptionsType) ?? '');
    setTabIndex(
      status === 'all'
        ? 0
        : status === 'open'
        ? 1
        : status === 'review'
        ? 2
        : status === 'merged'
        ? 3
        : 4
    );
  }, [contributorData, issueOptions, repoList, teamData]);

  useEffect(() => {
    setBranches(debouncedBranchInput.trim());
    handleScrollToTop();
    handleMetricFilterBy(
      'branchFilter',
      'branches',
      debouncedBranchInput.trim(),
      null
    );
  }, [debouncedBranchInput]);

  return (
    <Grid
      templateColumns={'repeat(2, 1fr)'}
      className="fitler-container"
      w={'full'}
      flexWrap={'wrap'}
    >
      <GridItem colStart={1} colEnd={3}>
        <Flex justifyContent={'space-between'}>
          <SubHeading
            heading={heading}
            chartKey={chartKey}
            getOptions={getOptions}
            tooltip="Access comprehensive information about all pull requests across your repositories in one centralized view. Filter PRs by status (All, WIP, In Review, Merged, Rejected), team, or custom criteria using mutiple filters to quickly find what matters most.Track PR lifecycles with timeline indicators showing how long each PR has spent in different stages. Use this table to identify stalled PRs, monitor team contributions, and ensure code reviews are distributed efficiently."
          />
          {!['contributors', 'deliverables'].includes(
            location.pathname.split('/')[1]
          ) && (
            <Flex alignSelf={'end'} gap={2}>
              <Tooltip
                label={
                  downloadAllowed
                    ? 'Download Data'
                    : 'Upgrade plan to download data'
                }
                hasArrow
              >
                <IconButton
                  isDisabled={!downloadAllowed || isDownloadingCSV}
                  icon={
                    <DownloadIcon
                      color={colors.text.secondary}
                      width={'16px'}
                      height={'16px'}
                    />
                  }
                  aria-label="Download CSV"
                  justifySelf={'self-end'}
                  alignSelf={'self-end'}
                  borderRadius={'4px'}
                  size={'md'}
                  variant={'outline'}
                  border={'1px'}
                  borderColor={'#E5E5E5'}
                  onClick={handleDownloadCSV}
                  cursor={'pointer'}
                />{' '}
              </Tooltip>
              <DateFilter
                isDetailsPage={searchParams.get('global') !== 'true'}
                isFullHeight
              />
            </Flex>
          )}
        </Flex>
        {state?.headingText && (
          <ComponentBanner
            signalHeadingText={state?.headingText}
            tooltip={state?.tooltip}
          />
        )}
      </GridItem>
      {metricFilterBy.showFilters ? (
        <>
          <GridItem
            colStart={1}
            colEnd={3}
            mb={4}
            mt={5}
            display={'flex'}
            flexWrap={'wrap'}
            gap={4}
            justifyContent={'space-between'}
            w={'full'}
            zIndex={10}
          >
            <Flex gap={4}>
              <SearchBar clearSearchBar={clearSearchBar} />
              <Flex
                alignSelf={'end'}
                gap={
                  [
                    'teams',
                    'repos',
                    'authors',
                    'feedback_response',
                    'review_feedback',
                    'review_response',
                    'pr_age',
                    'reviewers',
                    'pr_size',
                    'prIterations',
                  ].includes(showOption as string)
                    ? 2
                    : 1
                }
              >
                <Box pr={1} alignSelf={'end'}>
                  <Text fontSize={'xs'} color={'grey'}>
                    Team
                  </Text>
                  <Select
                    options={[
                      { label: 'Organisation', value: '', depth: 0 } as Option,
                      ...visibleTeams,
                    ]}
                    chakraStyles={filterStyles}
                    placeholder="Select Team"
                    value={teamValue}
                    expandedTeamPaths={expandedTeamPaths}
                    toggleTeamExpand={toggleTeamExpand}
                    components={{ Option: TeamHierarchySelectOption }}
                    onChange={(input: Option) => {
                      const modifiedValue = input?.value ?? '';
                      handleScrollToTop();
                      handleMetricFilterBy('teams', '', modifiedValue, null);
                      setTeamId(modifiedValue);
                      setClearSearchBar(!clearSearchBar);
                      handleTeamOptionChange(modifiedValue);
                    }}
                  />
                </Box>

                <Box alignSelf={'end'}>
                  <Text fontSize={'xs'} color={'grey'}>
                    Filter By
                  </Text>
                  <Select
                    options={getOptions('prTable')}
                    chakraStyles={filterStyles}
                    placeholder="Filter"
                    components={customFilterSelect}
                    value={defaultValue}
                    onChange={(input: Option) => {
                      handleDefaultOptionChange(
                        input?.label as string,
                        input?.value
                      );
                      setFirstOption(input?.value);
                      if (
                        input?.value !== 'teams' &&
                        input?.value !== 'repos' &&
                        input?.value !== 'authors' &&
                        input?.value !== 'prFeedbackResponsivenessType' &&
                        input?.value !== 'review_feedback' &&
                        input?.value !== 'prReviewResponsivenessType' &&
                        input?.value !== 'pr_age' &&
                        input?.value !== 'reviewers' &&
                        input?.value !== 'prIterations' &&
                        input?.value !== 'pr_size'
                      ) {
                        handleScrollToTop();
                        setClearSearchBar(!clearSearchBar);
                        setSecondOption('');
                        setBranches('');
                        setBranchInput('');
                        handleShowOptionChange('');
                        if (
                          input?.value === 'merged_with_review' ||
                          input?.value === 'merged_wo_review'
                        ) {
                          setMetricKey(
                            input?.value === 'merged_with_review'
                              ? 'merged_with_review'
                              : 'merged_without_review'
                          );
                        }
                        handleMetricFilterBy(
                          'prFilters',
                          input?.value,
                          input?.value,
                          null
                        );
                      } else {
                        handleShowOptionChange(input?.value);
                        handleSecondaryDefaultOptionChange('', '');
                        handleSecondaryDefaultMultiOptionChange([]);
                      }
                    }}
                  />
                </Box>
                <Flex gap={2} alignItems={'end'} justifyContent={'end'}>
                  {[
                    'teams',
                    'authors',
                    'prFeedbackResponsivenessType',
                    'review_feedback',
                    'prReviewResponsivenessType',
                    'pr_age',
                    'prIterations',
                    'reviewers',
                    'pr_size',
                  ].includes(showOption) ? (
                    <Select
                      // components={customRepoOption}
                      options={getOptions(showOption)}
                      chakraStyles={
                        ['repos', 'authors', 'reviewers'].includes(
                          showOption ?? ''
                        )
                          ? repoauthorFilterStyles
                          : filterStyles
                      }
                      placeholder={handleSelectPlaceHolder(showOption)}
                      value={
                        secondaryDefaultValue?.value
                          ? secondaryDefaultValue
                          : null
                      }
                      isDisabled={
                        ([
                          'prReviewResponsivenessType',
                          'review_feedback',
                          'prFeedbackResponsivenessType',
                        ].includes(showOption) &&
                          ['PR_AGEING_DISTRIBUTION'].includes(chartKey)) ||
                        (['pr_age'].includes(showOption) &&
                          [
                            'TOP_REVIEWER_DISTRIBUTION',
                            'PR_REVIEW',
                            'PR_REVIEWED',
                            'PR_HEALTH_INDEXES',
                            'LOC_VS_PR_CYCLE_TIME',
                            'PR_CYCLE_TIME_BREAKDOWN',
                            // below is for working agreements
                            'COMMIT_WITHOUT_REVIEW',
                            'PR_REVIEW_TIME',
                            'FIRST_TIME_REVIEW',
                            // below is for teams page
                            'PR_MERGE',
                            'PR_OPEN',
                            'MERGE_WITHOUT_REVIEW',
                            'PR_CYCLE_TIME',
                          ].includes(chartKey))
                      }
                      onChange={(input: Option) => {
                        setSecondOption(input?.value);
                        handleSecondaryDefaultMultiOptionChange([]);
                        handleScrollToTop();
                        setClearSearchBar(!clearSearchBar);
                        handleMetricFilterBy(
                          'prFilters',
                          showOption,
                          input?.value,
                          null
                        );
                        handleSecondaryDefaultOptionChange(
                          input?.label as string,
                          input?.value
                        );
                      }}
                    />
                  ) : showOption === 'repos' ? (
                    <Select
                      // components={customRepoOption}
                      options={getOptions(showOption)}
                      chakraStyles={
                        ['repos', 'authors', 'reviewers'].includes(
                          showOption ?? ''
                        )
                          ? repoauthorFilterStyles
                          : filterStyles
                      }
                      placeholder={handleSelectPlaceHolder(showOption)}
                      value={secondaryDefaultMultiValue}
                      isMulti={true}
                      onChange={(input: Option[]) => {
                        handleSecondaryDefaultMultiOptionChange(input);
                        setSecondOption(
                          input?.map((item: Option) => item.value).join(',')
                        );
                        handleScrollToTop();
                        setClearSearchBar(!clearSearchBar);
                        handleMetricFilterBy(
                          'prFilters',
                          showOption,
                          input?.map((item: Option) => item.value).join(','),
                          null
                        );
                      }}
                    />
                  ) : (
                    <></>
                  )}
                  <Box>
                    <Text fontSize={'xs'} color={'text.secondary2'}>
                      Branch Names (comma separated)
                    </Text>
                    <Input
                      value={branchInput}
                      onChange={(e) => setBranchInput(e.target.value)}
                      placeholder="e.g. main, develop, feature-xyz"
                      size="md"
                      width="200px"
                      borderColor="gray.300"
                    />
                  </Box>
                </Flex>

                {!['contributors', 'deliverables'].includes(
                  location.pathname.split('/')[1]
                ) && (
                  <Tooltip hasArrow label="Reset filters">
                    <IconButton
                      icon={<CircleCheck color={colors.text.secondary} />}
                      aria-label="Reset filters"
                      justifySelf={'self-end'}
                      alignSelf={'self-end'}
                      borderRadius={'4px'}
                      size={'md'}
                      variant={'outline'}
                      border={'1px'}
                      borderColor={'#E5E5E5'}
                      onClick={() => {
                        setHeading('PR Details Table');
                        setMetric('PR_UPDATED');
                        setChartKey('PR_UPDATED');
                        setMetricKey('');
                        setFirstOption('');
                        setSecondOption('');
                        setBranches('');
                        setBranchInput('');
                        setTeamId('');
                        setStatus('');
                        handleShowOptionChange('');
                        setClearSearchBar(!clearSearchBar);
                        handleDefaultOptionChange('All', '');
                        handleTeamOptionChange('');
                        setTabIndex(0);
                        metricFilterBy.resetFilters();
                      }}
                      cursor={'pointer'}
                    />
                  </Tooltip>
                )}
              </Flex>
            </Flex>
          </GridItem>

          <GridItem colStart={1} colEnd={3} mb={4}>
            <Tabs
              variant="unstyled"
              index={tabIndex}
              onChange={handlePRStatusChange}
            >
              <TabList borderBottom="1px solid" borderColor="gray.200">
                {['All', 'WIP', 'In Review', 'Merged', 'Rejected'].map(
                  (label, index) => (
                    <Tab
                      key={label}
                      fontSize="sm"
                      fontFamily="heading"
                      fontWeight="semibold"
                      lineHeight="17px"
                      color="text.secondary3"
                      borderBottom="2px solid transparent"
                      _selected={{
                        color: 'primary',
                        borderBottom: '2px solid',
                        borderColor: 'primary',
                      }}
                    >
                      {label}{' '}
                      {tabIndex === index &&
                        isFetched &&
                        `(${totalDBrows ?? 0})`}
                    </Tab>
                  )
                )}
              </TabList>
            </Tabs>
          </GridItem>
        </>
      ) : (
        <Box my={10}></Box>
      )}
    </Grid>
  );
}
